import { defaultServiceLanguage, type ServiceLanguage } from "@albertine/i18n";
import { createContext, type ReactNode, use } from "react";

import { type ChatMessageLabels, defaultChatMessageLabels } from "./labels";

/**
 * The host's own image viewer. A tapped image the host `canOpen` goes to
 * `open`; any other image falls back to the web lightbox.
 */
export type HostImageViewer = {
    canOpen: (url: string) => boolean;
    open: (url: string) => void;
};

type ChatContextValue = {
    lang: ServiceLanguage;
    labels: ChatMessageLabels;
    onImageLightboxChange?: (open: boolean) => void;
    hostImageViewer?: HostImageViewer;
};

const ChatContext = createContext<ChatContextValue>({
    lang: defaultServiceLanguage,
    labels: defaultChatMessageLabels,
});

type ChatProviderProps = {
    children: ReactNode;
    lang?: ServiceLanguage;
    labels?: ChatMessageLabels;
    onImageLightboxChange?: (open: boolean) => void;
    hostImageViewer?: HostImageViewer;
};

export function ChatProvider({
    children,
    lang = defaultServiceLanguage,
    labels = defaultChatMessageLabels,
    onImageLightboxChange,
    hostImageViewer,
}: ChatProviderProps) {
    return (
        <ChatContext value={{ lang, labels, onImageLightboxChange, hostImageViewer }}>
            {children}
        </ChatContext>
    );
}

export function useChat() {
    return use(ChatContext);
}
